/**
 *  ShopAnalysis script file
 **/
import { analyses, widgets } from '@/api/widgets'
import apiService from '@/api/apiService'
import customFormatters from '@/filters/customFormatters'

function stringCompare (a, b) {
  if (a > b) {
    return 1
  }
  if (a < b) {
    return -1
  }
  return 0
}

export default {
  name: 'ShopAnalysis',
  mixins: [customFormatters],
  components: {
    CollapsiblePercentBars: () => import(/* webpackChunkName: "CollapsiblePercentBars" */'@/components/collapsible-percent-bars/CollapsiblePercentBars.vue'),
    CompetitorsWidget: () => import(/* webpackChunkName: "CompetitorsWidget" */'@/components/competitors-widget/CompetitorsWidget.vue'),
    DoughnutChart: () => import(/* webpackChunkName: "DoughnutChart" */'@/components/charts/doughnut-chart/DoughnutChart.vue')
  },
  props: {
    center: {
      type: Object,
      required: true
    },
    areas: {
      type: Array,
      required: true
    },
    areatype: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      loading: true
    }
  },
  computed: {
    centerid () {
      return this.$route.params.id
    },
    analysisKey () {
      return this.$route.params.type
    },
    analysis () {
      return analyses.find(a => a.key === this.analysisKey)
    },
    allAreasLabel () {
      return this.areatype === 'grids'
        ? 'Todos los grids'
        : 'Todos los códigos postales'
    },
    taglist () {
      return this.areas.map(a => (
        a === 'all'
          ? { id: 'all', name: this.allAreasLabel }
          : { id: a, name: a }
      ))
    },
    widgets () {
      return widgets.filter(w => this.analysis.widgets.indexOf(w.key) !== -1)
    }
  },
  watch: {
    areatype () {
      this.fetchWidgetData()
    },
    areas () {
      this.fetchWidgetData()
    }
  },
  mounted () {
    this.fetchWidgetData()
  },
  methods: {
    updateScrollbar () {
      this.$emit('toggle')
    },
    async fetchWidgetData () {
      this.loading = true
      try {
        const { acum, length } = await apiService.getWidgetData({
          centerid: this.centerid,
          areatype: this.areatype,
          areas: this.areas,
          widgets: this.widgets
        })
        for (const widget of this.widgets) {
          for (const field of widget.fields) {
            const div = widget.average ? length[field.field] || 1 : 1
            field.value = (acum[field.field] || 0) / div
          }
          widget.fields.sort((a, b) => {
            if (widget.display === 'multi-bar-chart') {
              return 0
            }
            if (b.value === a.value) {
              return stringCompare(a.label, b.label)
            }
            return b.value - a.value
          })
          if (widget.display === 'multi-bar-chart') {
            widget.barChartData = this.prepareBarChartData(widget)
          }
          if (widget.display === 'percent-bar-sum' || widget.display === 'doughnut-chart') {
            widget.sumData = this.preparePercentBarSum(widget)
          }
          if (widget.display === 'doughnut-chart') {
            widget.sumData = [
              {
                data: widget.sumData.map(d => {
                  d.key = d.label
                  return d
                })
              }
            ]
          }
        }
      } catch (err) {
        // eslint-disable-next-line
        console.error('[shop-analysis] Error fetching widget data', err)
      }
      this.loading = false
      this.$nextTick(() => {
        this.updateScrollbar()
      })
    },
    preparePercentBarSum (widget) {
      const categories = widget.config.categories
      const labels = widget.config.labels
      const colors = widget.config.colors
      const groups = {}
      for (const regex of categories) {
        const category = regex.source.replace('^', '').replace('$', '')
        groups[category] = groups[category] || {
          field: category,
          label: labels[category],
          color: colors[category],
          value: 0
        }
        for (const field of widget.fields) {
          if (field.field.match(regex)) {
            groups[category].value += field.value
          }
        }
      }
      let res = Object.values(groups)
      if (widget.config.sort === undefined || widget.config.sort) {
        res = res.sort((a, b) => {
          if (b.value === a.value) {
            return stringCompare(a.label, b.label)
          }
          return b.value - a.value
        })
      }
      return res
    },
    prepareBarChartData (widget) {
      const categories = widget.config.categories
      const groups = {}
      for (const regex of categories) {
        const category = regex.source.replace('^', '').replace('$', '')
        groups[category] = groups[category] || {
          category,
          value: []
        }
        for (const field of widget.fields) {
          if (field.field.match(regex)) {
            groups[category].value.push({
              value: field.value,
              category: field.field.replace(category, '')
            })
          }
        }
      }
      return Object.values(groups)
    }
  }
}
